import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const ModernGallery = ({ images }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loadedImages, setLoadedImages] = useState({});
  const [isMobile, setIsMobile] = useState(false);

  // Handle window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleImageLoad = (imageId) => {
    setLoadedImages(prev => ({ ...prev, [imageId]: true }));
  };

  const handleImageClick = (image) => {
    setSelectedImage(image);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = useCallback(() => {
    setSelectedImage(null);
    setIsModalOpen(false);
    document.body.style.overflow = 'auto';
  }, []);

  const goToPrevious = useCallback(() => {
    if (!selectedImage || !images.length) return;
    const currentIndex = images.findIndex(img => img.id === selectedImage.id);
    const prevIndex = currentIndex === 0 ? images.length - 1 : currentIndex - 1;
    setSelectedImage(images[prevIndex]);
  }, [selectedImage, images]);

  const goToNext = useCallback(() => {
    if (!selectedImage || !images.length) return;
    const currentIndex = images.findIndex(img => img.id === selectedImage.id);
    const nextIndex = currentIndex === images.length - 1 ? 0 : currentIndex + 1;
    setSelectedImage(images[nextIndex]);
  }, [selectedImage, images]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isModalOpen) return;
      if (e.key === 'Escape') {
        closeModal();
      } else if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen, closeModal, goToPrevious, goToNext]);

  // Auto-advance active index every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % images.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [images.length]);

  const formatTitle = (title) => {
    const words = title.split(' ');
    if (words.length === 1) {
      return { first: words[0], rest: '' };
    }
    return { first: words[0], rest: words.slice(1).join(' ') };
  };

  // Get visible images based on screen size - expanded image moves to edge
  const getVisibleImages = () => {
    if (isMobile) {
      // Mobile: show 3 images total (1 expanded, 2 collapsed)
      // Expanded image is always at position 1 (middle)
      let start = activeIndex - 1;
      if (start < 0) start = 0;
      if (start + 3 > images.length) start = Math.max(0, images.length - 3);
      return images.slice(start, start + 3);
    }
    
    // Desktop & Tablet: show 7 images total (1 expanded, 6 collapsed)
    // Expanded image moves to the LEFT edge when index is small
    // Moves to the RIGHT edge when index is large
    let start;
    if (activeIndex < 3) {
      // Start from beginning, expanded will be near left
      start = 0;
    } else if (activeIndex > images.length - 4) {
      // Start from end, expanded will be near right
      start = Math.max(0, images.length - 7);
    } else {
      // Expanded in middle range, shift based on direction
      start = activeIndex - 1;
    }
    
    // Ensure we don't go out of bounds
    start = Math.max(0, Math.min(start, images.length - 7));
    return images.slice(start, start + 7);
  };

  const visibleImages = getVisibleImages();
  
  // Find position of active image in visible array
  const activePosition = visibleImages.findIndex(img => img.id === images[activeIndex]?.id);

  return (
    <>
      {/* Horizontal Accordion Gallery */}
      <div className="w-full">
        <div className={`flex gap-2 md:gap-2 w-full h-[280px] sm:h-[320px] md:h-[400px] lg:h-[450px]`}>
          {visibleImages.map((image, idx) => {
            const originalIndex = images.findIndex(img => img.id === image.id);
            const isExpanded = activeIndex === originalIndex;
            const { first, rest } = formatTitle(image.title);
            
            // Calculate flex values based on position for smooth edge movement
            let flexValue;
            if (isExpanded) {
              flexValue = 'flex-[5]';
            } else {
              // Collapsed images get smaller flex on desktop
              flexValue = isMobile ? 'flex-[1]' : 'flex-[0.6]';
            }
            
            return (
              <motion.div
                key={image.id}
                onMouseEnter={() => !isMobile && setActiveIndex(originalIndex)}
                onClick={() => setActiveIndex(originalIndex)}
                className={`relative rounded-xl sm:rounded-2xl overflow-hidden cursor-pointer transition-all duration-500 ease-in-out ${flexValue}`}
              >
                {/* Loading spinner */}
                {!loadedImages[image.id] && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-200 z-10">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 border-4 border-[#fbbf24] border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
                
                {/* Image - Click to expand to full screen */}
                <img
                  src={image.src}
                  alt={image.title}
                  className={`w-full h-full object-cover transition-transform duration-700 ${
                    isExpanded ? 'scale-105' : 'scale-100'
                  } ${loadedImages[image.id] ? 'opacity-100' : 'opacity-0'}`}
                  onLoad={() => handleImageLoad(image.id)}
                  loading="lazy"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleImageClick(image);
                  }}
                />
                
                {/* Dark overlay for expanded item */}
                {isExpanded && (
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                )}
                
                {/* Content overlay - only shown when expanded */}
                {isExpanded && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1, duration: 0.4 }}
                    className="absolute bottom-3 left-3 right-3 sm:bottom-5 sm:left-5 sm:right-5 text-white z-10"
                  >
                    <h2 className="text-xs sm:text-sm md:text-base lg:text-lg font-bold leading-tight">
                      {first}{' '}
                      {rest && <span className="italic font-semibold">{rest}</span>}
                    </h2>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleImageClick(image);
                      }}
                      className="mt-1 sm:mt-2 px-2 py-0.5 sm:px-3 sm:py-1 bg-white text-gray-900 rounded-full text-[8px] sm:text-[10px] font-medium hover:scale-105 transition-all duration-300 shadow-lg flex items-center gap-1"
                      aria-label="Click to view full image"
                    >
                      <svg className="w-2 h-2 sm:w-2.5 sm:h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      Click to view
                    </button>
                  </motion.div>
                )}

                {/* Collapsed image label - shows on hover for desktop */}
                {!isExpanded && !isMobile && (
                  <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <span className="text-white text-[10px] font-medium bg-black/50 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                      Click to view
                    </span>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Lightbox Modal for full image view */}
      <AnimatePresence>
        {isModalOpen && selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-2 sm:p-4"
            onClick={closeModal}
          >
            {/* Close button */}
            <button
              onClick={closeModal}
              className="absolute top-2 right-2 sm:top-4 sm:right-4 z-10 bg-black/50 hover:bg-black/70 rounded-full p-1.5 sm:p-2 transition-all duration-300"
              aria-label="Close modal"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Previous button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToPrevious();
              }}
              className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 rounded-full p-1.5 sm:p-2 transition-all duration-300 z-10"
              aria-label="Previous image"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            {/* Next button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToNext();
              }}
              className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 rounded-full p-1.5 sm:p-2 transition-all duration-300 z-10"
              aria-label="Next image"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            {/* Image container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="relative max-w-6xl max-h-[90vh] w-auto h-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={selectedImage.src}
                alt={selectedImage.title}
                className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              />
              
              {/* Image title/caption bar */}
              {selectedImage.title && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent rounded-b-lg p-2 sm:p-3">
                  <p className="text-white text-center text-[10px] sm:text-xs font-medium">
                    {selectedImage.title}
                  </p>
                </div>
              )}
              
              {/* Image counter */}
              <div className="absolute top-2 left-2 sm:top-4 sm:left-4 bg-black/50 backdrop-blur-sm rounded-full px-2 py-0.5 sm:px-3 sm:py-1">
                <span className="text-white text-[8px] sm:text-[10px] font-medium">
                  {images.findIndex(img => img.id === selectedImage.id) + 1} / {images.length}
                </span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ModernGallery;
